import type { AnalyticsRecord } from '@/utils/services/analytics/AnalyticsDataService';

import type {
    SocialProfileNetworkType,
    SocialProfilePermissionsType
} from '@/types/socialProfileType';
import type {
    KpiSplit,
    Post,
    PostFollowers,
    StatsConfig
} from '@/components/composite/MediaViewer/components/MediaViewerStats/types';

import { FACEBOOK, TIKTOK_BUSINESS, YOUTUBE } from '@/lib/socialProfile';
import { EXCLUDED_NO_FOLLOWERS_KPIS } from '@/components/composite/MediaViewer/components/MediaViewerStats/const';

import { MediaViewerKPI } from '@/components/base/Kpi/MediaViewerKpi';
import AnalyticsGrid, { GRID_STYLES } from '@/components/layouts/wrappers/AnalyticsGrid';

const supportsMergedData = (socialNetworkType: SocialProfileNetworkType): boolean => {
    return (
        socialNetworkType === FACEBOOK ||
        socialNetworkType === TIKTOK_BUSINESS ||
        socialNetworkType === YOUTUBE
    );
};

type KpiGridItemProps = {
    config: StatsConfig;
    isLoading: boolean;
    isUserLoading: boolean;
    isLoadingPermission: boolean;
    isLoadingAnalytics: boolean;
    shouldUseAnalyticsV2: boolean;
    analytics: AnalyticsRecord | null;
    post: Post | null;
    getKpiValue: (configId: string) => unknown;
    getKpiSplit: (configId: string) => KpiSplit;
    socialNetworkType: SocialProfileNetworkType;
    postFollowers: PostFollowers;
    permissions: SocialProfilePermissionsType[];
    userNumbersFormat: string;
};

export const KpiGridItem = ({
    config,
    isLoading,
    isUserLoading,
    isLoadingPermission,
    isLoadingAnalytics,
    shouldUseAnalyticsV2,
    analytics,
    post,
    getKpiValue,
    getKpiSplit,
    socialNetworkType,
    postFollowers,
    permissions,
    userNumbersFormat
}: KpiGridItemProps) => {
    const dataSource = shouldUseAnalyticsV2 ? analytics : post;
    const hasData = !(isLoading || isUserLoading) && dataSource;

    const excludedKpis =
        EXCLUDED_NO_FOLLOWERS_KPIS?.[
            socialNetworkType as keyof typeof EXCLUDED_NO_FOLLOWERS_KPIS
        ] ?? [];

    const kpiData = hasData
        ? {
              value: getKpiValue(config?.id),
              split: getKpiSplit(config.id),
              data_estimated: excludedKpis.includes(config.id as never) && postFollowers?.estimated
          }
        : {};

    const kpiConfig = {
        ...config,
        mergedData: supportsMergedData(socialNetworkType) ? config?.mergedData : false,
        network: socialNetworkType,
        permissions
    };

    const LoaderComponent = config.loader;

    return (
        <AnalyticsGrid.Item size={config.size} gridStyle={GRID_STYLES.MEDIA_VIEWER}>
            <MediaViewerKPI
                isLoading={
                    isLoading ||
                    isUserLoading ||
                    isLoadingPermission ||
                    (shouldUseAnalyticsV2 && isLoadingAnalytics)
                }
                data={kpiData}
                config={kpiConfig}
                userNumbersFormat={{ number: userNumbersFormat }}
                variant="noShadow"
                renderLoader={() => (LoaderComponent ? <LoaderComponent /> : null)}>
                {null}
            </MediaViewerKPI>
        </AnalyticsGrid.Item>
    );
};
